import { useMemo, useState } from "preact/hooks";
import type { RadarEntity } from "../data/provider";
import type { SkillGroupId, TrendStatus } from "../data/schema";
import { t } from "../i18n";

const GROUP_COLOR: Record<string, string> = {
  "scientific-lab": "#123a73",
  "digital-data": "#0057b8",
  research: "#3d5a80",
  "green-sustainability": "#2e7d4f",
  "entrepreneurial-innovation": "#b45309",
  "transferable-professional": "#5b4b8a",
};

const SIZE = 860;
const CX = 430;
const CY = 430;
const MAX_R = 348;
const RING_BANDS = [
  { factor: 0.92, fill: "#f3f6fa" },
  { factor: 0.68, fill: "#eaf0f6" },
  { factor: 0.42, fill: "#e1eaf3" },
];
const RING_STROKES = [0.42, 0.68, 0.92];

function ringFor(status: TrendStatus): number {
  if (status === "established") return 0.32;
  if (status === "transforming") return 0.5;
  if (status === "growing") return 0.66;
  return 0.84;
}

function colorFor(entity: RadarEntity): string {
  return GROUP_COLOR[entity.groupId ?? ""] ?? "#123a73";
}

function typeLabel(type: RadarEntity["type"]): string {
  if (type === "technology") return t("radar.technologies");
  if (type === "skill") return t("radar.skills");
  if (type === "career") return t("radar.careers");
  return t("radar.sectors");
}

function shortLabel(name: string): string {
  return name.length > 26 ? `${name.slice(0, 24)}…` : name;
}

function labelOffset(x: number, y: number, r: number): { dx: number; dy: number; anchor: "start" | "middle" | "end" } {
  const vx = x - CX;
  const vy = y - CY;
  const len = Math.hypot(vx, vy) || 1;
  const outward = r + 10;
  const dx = (vx / len) * outward;
  const dy = (vy / len) * outward;
  const anchor = Math.abs(dx) < 8 ? "middle" : dx > 0 ? "start" : "end";
  return { dx, dy, anchor };
}

function degree(entity: RadarEntity): number {
  return (
    entity.relatedSkillIds.length +
    entity.relatedCareerIds.length +
    entity.relatedTechnologyIds.length +
    entity.relatedSectorIds.length
  );
}

function nodeRadius(entity: RadarEntity, selected: boolean): number {
  const base = 6.5 + Math.min(degree(entity), 8) * 0.35;
  return selected ? base + 3 : base;
}

type Placed = { entity: RadarEntity; x: number; y: number };

function placeEntities(entities: RadarEntity[]): Placed[] {
  const grouped = new Map<string, RadarEntity[]>();
  for (const entity of entities) {
    const key = entity.groupId && GROUP_COLOR[entity.groupId] ? entity.groupId : entity.type;
    const list = grouped.get(key) ?? [];
    list.push(entity);
    grouped.set(key, list);
  }
  const orderedKeys = [
    ...Object.keys(GROUP_COLOR).filter((k) => grouped.has(k)),
    ...[...grouped.keys()].filter((k) => !(k in GROUP_COLOR)),
  ];
  const total = Math.max(entities.length, 1);
  const placed: Placed[] = [];
  let start = -Math.PI / 2;
  for (const key of orderedKeys) {
    const list = grouped.get(key) ?? [];
    const slice = (Math.PI * 2 * list.length) / total;
    list.forEach((entity, i) => {
      const inner = list.length === 1 ? 0.5 : (i + 0.55) / (list.length + 0.1);
      const angle = start + slice * inner;
      const r = ringFor(entity.trendStatus) * MAX_R;
      placed.push({
        entity,
        x: CX + Math.cos(angle) * r,
        y: CY + Math.sin(angle) * r,
      });
    });
    start += slice;
  }
  for (let pass = 0; pass < 10; pass++) {
    for (let i = 0; i < placed.length; i++) {
      for (let j = i + 1; j < placed.length; j++) {
        const a = placed[i];
        const b = placed[j];
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const dist = Math.hypot(dx, dy) || 0.01;
        const min = 22;
        if (dist >= min) continue;
        const push = ((min - dist) / 2) * 0.65;
        const nx = dx / dist;
        const ny = dy / dist;
        a.x -= nx * push;
        a.y -= ny * push;
        b.x += nx * push;
        b.y += ny * push;
        for (const p of [a, b]) {
          const vx = p.x - CX;
          const vy = p.y - CY;
          const len = Math.hypot(vx, vy) || 1;
          const clamped = Math.min(MAX_R * 0.9, Math.max(MAX_R * 0.22, len));
          p.x = CX + (vx / len) * clamped;
          p.y = CY + (vy / len) * clamped;
        }
      }
    }
  }
  return placed;
}

export function TrendRadar({
  entities,
  selectedId,
  connected,
  showLabels,
  onSelect,
}: {
  entities: RadarEntity[];
  selectedId?: string;
  connected: Set<string>;
  showLabels?: boolean;
  onSelect: (id: string) => void;
}) {
  const [hoveredId, setHoveredId] = useState<string | undefined>();
  const placed = useMemo(() => placeEntities(entities), [entities]);
  const selected = placed.find((p) => p.entity.id === selectedId);
  const hovered = placed.find((p) => p.entity.id === hoveredId);
  const tip = hovered;
  const connectionsOn = connected.size > 1;
  const labelsOn = showLabels ?? entities.length <= 20;

  return (
    <div class="obs-radar-stage" onMouseLeave={() => setHoveredId(undefined)}>
      <svg
        class="obs-radar-svg"
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        role="group"
        aria-label={t("radar.ringGuide")}
      >
        <defs>
          <filter id="obs-node-halo" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="0" stdDeviation="2.2" flood-color="#123a73" flood-opacity="0.22" />
          </filter>
        </defs>
        {RING_BANDS.map((band) => (
          <circle key={band.factor} class="obs-radar-band" cx={CX} cy={CY} r={band.factor * MAX_R} fill={band.fill} />
        ))}
        <line class="obs-radar-axis" x1={CX - MAX_R} y1={CY} x2={CX + MAX_R} y2={CY} />
        <line class="obs-radar-axis" x1={CX} y1={CY - MAX_R} x2={CX} y2={CY + MAX_R} />
        {RING_STROKES.map((f) => (
          <circle key={f} class="obs-radar-ring" cx={CX} cy={CY} r={f * MAX_R} fill="none" />
        ))}
        <text class="obs-radar-ring-label" x={CX} y={CY + 5} text-anchor="middle">
          {t("radar.core")}
        </text>
        {selected && connectionsOn
          ? placed
              .filter((p) => connected.has(p.entity.id) && p.entity.id !== selected.entity.id)
              .map((p) => (
                <line
                  key={`link-${p.entity.id}`}
                  class="obs-radar-link"
                  x1={selected.x}
                  y1={selected.y}
                  x2={p.x}
                  y2={p.y}
                />
              ))
          : null}
        {placed.map(({ entity, x, y }, index) => {
          const dim = connectionsOn && !connected.has(entity.id);
          const isSelected = entity.id === selectedId;
          const isHot = isSelected || entity.id === hoveredId;
          const r = nodeRadius(entity, isSelected);
          const showName = labelsOn || isHot;
          const offset = labelOffset(x, y, r);
          const crowded = labelsOn && placed.length > 12 && index % 2 === 1 && !isHot;
          return (
            <g
              key={entity.id}
              class={`obs-node${isSelected ? " is-selected" : ""}${dim ? " is-dim" : ""}`}
              tabIndex={0}
              role="button"
              aria-label={`${entity.name}, ${typeLabel(entity.type)}, ${t(`radar.${entity.trendStatus}`)}`}
              aria-pressed={isSelected}
              transform={`translate(${x} ${y})`}
              onClick={() => onSelect(entity.id)}
              onMouseEnter={() => setHoveredId(entity.id)}
              onFocus={() => setHoveredId(entity.id)}
              onBlur={() => setHoveredId((id) => (id === entity.id ? undefined : id))}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelect(entity.id);
                }
              }}
            >
              {isSelected ? <circle class="obs-node-halo" r={r + 8} /> : null}
              <circle r={r} fill={colorFor(entity)} filter={isHot ? "url(#obs-node-halo)" : undefined} />
              {showName && !crowded ? (
                <text class="obs-node-label" x={offset.dx} y={offset.dy + 3} text-anchor={offset.anchor}>
                  {shortLabel(entity.name)}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
      {tip ? (
        <div
          class="obs-radar-tooltip"
          style={{
            left: `${(tip.x / SIZE) * 100}%`,
            top: `${(tip.y / SIZE) * 100}%`,
          }}
        >
          <strong>{tip.entity.name}</strong>
          <span>
            {typeLabel(tip.entity.type)} · {t(`radar.${tip.entity.trendStatus}`)}
          </span>
          {tip.entity.groupId ? <span>{t(`group.${tip.entity.groupId}`)}</span> : null}
        </div>
      ) : null}
    </div>
  );
}

export const GROUP_IDS = Object.keys(GROUP_COLOR) as SkillGroupId[];
export { GROUP_COLOR };
